import { Component, NgZone, inject, OnInit } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { UIService } from 'src/app/rxjs-store/ui';
import { AuthService } from 'src/app/store/auth';
import { Store } from '@ngxs/store';
import { GlobalNavBarComponent } from './ui-components/global-nav-bar/global-nav-bar.component';
import { BottomSheetComponent, ActionDialogComponent } from './ui-kit';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import {
  NavigationBarsState,
  NavigationBarsService,
} from 'src/app/rxjs-store/navigation-bars';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ActionDialogConfig } from './models/ui';
import { DeviceState } from './store/device/device.state';
import { CommandPaletteComponent } from './layout/command-palette/command-palette.component';
import { CommandPaletteService } from './store/command-palette';
import { StateInspectorPanelComponent } from './state-inspector/state-inspector-panel/state-inspector-panel.component';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Router } from '@angular/router';
/**
 * Root App Component
 *
 * Handles application initialization, including:
 * - UI state initialization (theme, preferences)
 * - Auth state initialization (restore session)
 * - Signal-based states (AccountState auto-syncs with Firebase Auth)
 * - Initial navigation for authenticated users
 * - Navigation bars visibility management
 * - Global bottom sheet management
 * - Global backdrop management
 */
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    IonApp,
    IonRouterOutlet,
    GlobalNavBarComponent,
    BottomSheetComponent,
    ActionDialogComponent,
    CommandPaletteComponent,
    StateInspectorPanelComponent,
  ],
  animations: [
    trigger('backdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
})
export class AppComponent implements OnInit {
  // Navigation bar visibility observables
  bottomNavBarVisible$: Observable<boolean | null>;

  // Action dialog observables
  actionDialog$: Observable<ActionDialogConfig>;
  actionDialogVisible$: Observable<boolean>;

  // Backdrop observable
  backdrop$: Observable<boolean>;

  // Command palette service
  readonly commandPaletteService = inject(CommandPaletteService);

  constructor(
    private uiService: UIService,
    private authService: AuthService,
    private store: Store,
    private navBarsService: NavigationBarsService,
    private deviceState: DeviceState,
    private zone: NgZone,
    private router: Router
  ) {
    // Initialize navigation bar visibility selectors
    this.bottomNavBarVisible$ = this.store.select(
      NavigationBarsState.bottomNavBarVisible
    );

    // Initialize action dialog selectors
    // Use distinctUntilChanged with shallow comparison to prevent unnecessary emissions
    // when NGXS creates new object references but values haven't changed
    this.actionDialog$ = this.store.select(NavigationBarsState.actionDialog).pipe(
      distinctUntilChanged((prev, curr) => {
        // Shallow comparison of all properties except content (TemplateRef)
        // Content reference changes are legitimate and should trigger updates
        return prev.visible === curr.visible &&
               prev.title === curr.title &&
               prev.maxWidth === curr.maxWidth &&
               prev.showCloseButton === curr.showCloseButton &&
               prev.showDoneButton === curr.showDoneButton &&
               prev.closeOnBackdropClick === curr.closeOnBackdropClick &&
               prev.closeOnEscape === curr.closeOnEscape &&
               prev.content === curr.content;
      })
    );
    this.actionDialogVisible$ = this.store.select(
      NavigationBarsState.actionDialogVisible
    ).pipe(distinctUntilChanged());

    // Initialize backdrop selector with distinctUntilChanged
    this.backdrop$ = this.store.select(NavigationBarsState.backdrop).pipe(
      distinctUntilChanged()
    );
    // Initialize backdrop selector
    this.backdrop$ = this.store.select(NavigationBarsState.backdrop);

    // Initialize apple app site association
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        // Example url: https://blumemethod.app/tabs/tab2
        const slug = event.url.split(".app").pop();
        if (slug) {
          this.router.navigateByUrl(slug);
        }
        // If no match, do nothing - let regular routing
        // logic take over
      });
    });
  }

  ngOnInit(): void {
    // Clear user-initialization flag on app load
    // This ensures users go through /loading page on every page refresh
    // to reinitialize all signal-based states
    sessionStorage.removeItem('blume_user_init_complete');

    // Initialize Auth state - loads saved session and sets up Firebase listener
    this.authService.initialize();
  }

  closeActionDialog(): void {
    this.navBarsService.closeActionDialog();
  }

  onBackdropClick(): void {
    this.navBarsService.hideBackdrop();
  }

  closeCommandPalette(): void {
    this.commandPaletteService.close();
  }

  onCommandPaletteSearchChange(searchValue: string): void {
    this.commandPaletteService.setSearchValue(searchValue);
  }
}
